import React, { useState, useEffect } from 'react'
import { useAuth } from '../services/AuthContext'
import apiService from '../services/api'
import { EmmaBrandLink } from '../components/EmmaBrandLink'
import { isPaddleJsConfigured, openPaddleOverlayCheckout } from '../lib/paddleClient'
import type { Environments } from '@paddle/paddle-js'
import {
  classifyCheckoutRedirectUrl,
  getBillingBuildSnapshot,
  logCheckout,
} from '../lib/subscribeCheckoutDebug'

interface Plan {
  id: string
  name: string
  price: number
  limits: {
    inputTokens?: number
    outputTokens?: number
    monthlyUsd?: number
    sixHourUsd?: number
  }
  period: string
  description: string
  paddlePriceId: string | null
}

const SubscribePage: React.FC = () => {
  const { user, subscription, quotaStatus } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [plans, setPlans] = useState<Plan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  /** Set from API — checkout is created on the server; this mirrors server PADDLE_ENVIRONMENT. */
  const [paddleEnvironment, setPaddleEnvironment] = useState<'sandbox' | 'production' | null>(null)

  useEffect(() => {
    // Check for client parameter (premiere or desktop) and store in localStorage
    const urlParams = new URLSearchParams(window.location.search)
    const client = urlParams.get('client')
    if (client === 'premiere' || client === 'desktop') {
      localStorage.setItem('auth_client', client)
    }
    
    // Check for machineId parameter (from Premiere plugin) and store in localStorage
    const machineId = urlParams.get('machineId')
    if (machineId) {
      localStorage.setItem('auth_machineId', machineId)
    }
    
    // Check for temporary token from OAuth redirect (for Premiere clients)
    // Store it temporarily so checkout API can use it
    const tempToken = urlParams.get('token')
    if (tempToken && client === 'premiere') {
      localStorage.setItem('authToken', tempToken)
      // Clean up URL
      urlParams.delete('token')
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
      window.history.replaceState({}, document.title, newUrl)
    }

    const fetchPlans = async () => {
      logCheckout('subscribe.mount', {
        build: getBillingBuildSnapshot(),
        note: 'VITE_* vars are fixed at webapp build time. Heroku only affects the API server, not this S3/static bundle.',
      })
      try {
        const response = await apiService.getPlans()
        setPlans(response.plans || [])
        if (response.paddleEnvironment === 'sandbox' || response.paddleEnvironment === 'production') {
          setPaddleEnvironment(response.paddleEnvironment)
        }
        logCheckout('subscribe.plans.loaded', {
          paddleEnvironment: response.paddleEnvironment ?? '(missing from API — overlay gate may fail)',
          planCount: (response.plans || []).length,
          plansPaddlePriceIds: (response.plans || []).map((p: Plan) => ({
            id: p.id,
            paddlePriceId: p.paddlePriceId ?? null,
          })),
        })
      } catch (err) {
        console.error('Failed to fetch plans:', err)
        logCheckout('subscribe.plans.error', { err: String(err) })
        setError('Failed to load subscription plans')
      } finally {
        setPlansLoading(false)
      }
    }

    fetchPlans()
  }, [])

  useEffect(() => {
    logCheckout('subscribe.auth.snapshot', {
      hasUser: !!user,
      userId: user?.id ?? null,
      subscriptionPlan: subscription?.plan ?? null,
      subscriptionStatus: subscription?.status ?? null,
      paddleEnvironmentState: paddleEnvironment,
      paddleJsConfigured: isPaddleJsConfigured(),
      overlayEligible:
        isPaddleJsConfigured() && !!paddleEnvironment && !!user?.id,
    })
  }, [user, subscription, paddleEnvironment])

  const handleSubscribe = async (priceId: string, planId: string) => {
    setLoading(true)
    setLoadingPlanId(planId)
    setError('')

    try {
      const machineId = localStorage.getItem('auth_machineId') || undefined
      const client = localStorage.getItem('auth_client') || undefined

      const overlayGate = {
        isPaddleJsConfigured: isPaddleJsConfigured(),
        paddleEnvironment,
        hasUserId: !!user?.id,
      }

      logCheckout('subscribe.handleSubscribe.start', {
        priceId,
        planId,
        machineId: machineId ?? null,
        client: client ?? null,
        overlayGate,
        build: getBillingBuildSnapshot(),
      })

      // Downgrade / cancel-to-free still goes through the server (Paddle API + DB updates).
      if (priceId === 'free') {
        logCheckout('subscribe.branch', { branch: 'free_downgrade_server' })
        const { url } = await apiService.createCheckoutSession(priceId, machineId, client)
        const classified = classifyCheckoutRedirectUrl(url)
        logCheckout('subscribe.redirect', { branch: 'free_downgrade_server', url, classified })
        window.location.href = url
        return
      }

      // Paddle.js (client token) — same integration style as Paddle’s CodePen sandbox demos.
      // Avoids server-side transactions.create checkout.url domain approval for local dev.
      if (
        isPaddleJsConfigured() &&
        paddleEnvironment &&
        user?.id
      ) {
        const env: Environments = paddleEnvironment === 'production' ? 'production' : 'sandbox'
        const successUrl = `${window.location.origin}/account?success=true&processing=true`
        logCheckout('subscribe.branch', {
          branch: 'paddle_js_overlay',
          paddleJsEnvironment: env,
          successUrl,
          priceId,
        })
        await openPaddleOverlayCheckout({
          environment: env,
          priceId,
          customData: {
            userId: user.id,
            machineId: machineId ?? '',
            client: client ?? '',
          },
          successUrl,
        })
        return
      }

      logCheckout('subscribe.branch', {
        branch: 'hosted_checkout_server',
        reasonSkippedOverlay: !overlayGate.isPaddleJsConfigured
          ? 'VITE_PADDLE_CLIENT_TOKEN not in bundle'
          : !overlayGate.paddleEnvironment
            ? 'paddleEnvironment null (GET /plans missing paddleEnvironment?)'
            : !overlayGate.hasUserId
              ? 'user.id missing (not logged in?)'
              : 'unknown',
        overlayGate,
      })

      // Hosted checkout URL from server (secret API key).
      const { url } = await apiService.createCheckoutSession(priceId, machineId, client)
      const classified = classifyCheckoutRedirectUrl(url)
      logCheckout('subscribe.redirect', { branch: 'hosted_checkout_server', url, classified })
      window.location.href = url
    } catch (err: any) {
      logCheckout('subscribe.handleSubscribe.error', {
        message: err?.message || String(err),
        response: err?.response?.data,
        status: err?.response?.status,
      })
      setError(err.message || 'Failed to create checkout session')
    } finally {
      setLoadingPlanId(null)
      setLoading(false)
    }
  }

  const getButtonText = (plan: Plan): string => {
    const currentPlan = subscription?.plan || 'free'
    
    // If this is the current plan
    if (currentPlan === plan.id) {
      return 'Current Plan'
    }
    
    // If no subscription or on free plan
    if (!subscription || currentPlan === 'free') {
      return 'Subscribe'
    }
    
    // If user has a paid plan, determine if this is upgrade or downgrade
    const planHierarchy = ['free', 'basic', 'pro']
    const currentIndex = planHierarchy.indexOf(currentPlan)
    const targetIndex = planHierarchy.indexOf(plan.id)
    
    if (targetIndex > currentIndex) {
      return 'Upgrade'
    } else if (targetIndex < currentIndex) {
      return 'Downgrade'
    }
    
    return 'Subscribe'
  }

  const handleCancel = async () => {
    setLoading(true)
    setError('')

    try {
      await apiService.cancelSubscription()
      window.location.reload()
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription')
    } finally {
      setLoading(false)
    }
  }

  // Format price for display
  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `$${price}`
  }

  // Format period for display
  const formatPeriod = (period: string) => {
    if (period === '6 hours') return '/6 hours'
    if (period === 'month') return '/month'
    return `/${period}`
  }

  const usdToDisplayTokens = (usd: number) => Math.round(usd * 100000)
  const floorToStep = (value: number, step: number) => Math.floor(value / step) * step

  const renderPlanLimits = (plan: Plan) => {
    if (typeof plan.limits?.monthlyUsd === 'number') {
      const monthlyTokens = usdToDisplayTokens(plan.limits.monthlyUsd)
      const rawSixHourTokens = usdToDisplayTokens(plan.limits.sixHourUsd ?? 0)
      const sixHourTokens =
        plan.id === 'free'
          ? floorToStep(rawSixHourTokens, 500)
          : floorToStep(rawSixHourTokens, 1000)
      return [
        `${monthlyTokens.toLocaleString()} tokens per month`,
        `${sixHourTokens.toLocaleString()} tokens per 6 hour rolling window`,
      ]
    }

    return [
      `${(plan.limits?.inputTokens ?? 0).toLocaleString()} input tokens per ${plan.period}`,
      `${(plan.limits?.outputTokens ?? 0).toLocaleString()} output tokens per ${plan.period}`,
    ]
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '2rem 0' }}>
      <div className="container">
        <EmmaBrandLink />
        {paddleEnvironment === 'sandbox' && (
          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              textAlign: 'center',
              color: '#92400e',
              fontSize: '0.9375rem',
            }}
          >
            Billing mode: Paddle <strong>Sandbox</strong> (from server <code style={{ fontSize: '0.85em' }}>GET /plans</code>
            ).{' '}
            {isPaddleJsConfigured() ? (
              <>
                Subscriptions use <strong>Paddle.js</strong> overlay checkout (client token), like{' '}
                <a href="https://codepen.io/heymcgovern/pen/raBZRvz" target="_blank" rel="noreferrer">
                  Paddle’s CodePen
                </a>
                .
              </>
            ) : (
              <>
                Add <code style={{ fontSize: '0.85em' }}>VITE_PADDLE_CLIENT_TOKEN</code> for overlay checkout; otherwise
                checkout is created on the <strong>server</strong> (hosted redirect).
              </>
            )}
          </div>
        )}
        {paddleEnvironment === 'production' && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              textAlign: 'center',
              color: '#991b1b',
              fontSize: '0.9375rem',
            }}
          >
            Checkout uses Paddle <strong>Live</strong> (real charges). For local testing use sandbox keys +{' '}
            <code style={{ fontSize: '0.85em' }}>PADDLE_ENVIRONMENT=sandbox</code> on the server.
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            Choose Your Plan
          </h1>
          <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>
            Upgrade to unlock unlimited AI-powered video editing
          </p>
        </div>

        {error && (
          <div className="alert alert-error" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
            {error}
          </div>
        )}

        {/* Current Status */}
        {quotaStatus && (
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Current Usage</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Input Tokens
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {quotaStatus.usage.inputTokens} / {quotaStatus.limits.inputTokens}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Output Tokens
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {quotaStatus.usage.outputTokens} / {quotaStatus.limits.outputTokens}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Subscription */}
        {subscription && subscription.status === 'active' && (
          <div style={{ 
            background: 'white', 
            padding: '1.5rem', 
            borderRadius: '0.75rem', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Current Subscription</h3>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', textTransform: 'capitalize' }}>
              {subscription.plan} Plan
            </p>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
              Status: {subscription.status}
            </p>
            <button
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              {loading ? 'Canceling...' : 'Cancel Subscription'}
            </button>
          </div>
        )}

        {/* Pricing Plans */}
        {plansLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '1.25rem' }}>Loading plans...</div>
          </div>
        ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
            {plans.map((plan) => {
            const planLimits = renderPlanLimits(plan)
            return (
            <div key={plan.id} style={{ 
              background: 'white', 
              padding: '2rem', 
              borderRadius: '1rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              border: subscription && subscription.plan === plan.id ? '2px solid #10b981' : '2px solid #e5e7eb',
              position: 'relative'
            }}>
              {subscription && subscription.plan === plan.id && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '20px',
                  background: '#10b981',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  Current Plan
                </div>
              )}
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {plan.name}
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                {plan.description}
              </p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '3rem', fontWeight: 'bold' }}>
                  {formatPrice(plan.price)}
                </span>
                <span style={{ color: '#6b7280' }}>{formatPeriod(plan.period)}</span>
              </div>

              <ul style={{ listStyle: 'none', marginBottom: '2rem' }}>
                <li style={{ 
                  padding: '0.5rem 0', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <span style={{ 
                    color: '#10b981', 
                    marginRight: '0.5rem',
                    fontSize: '1.25rem'
                  }}>✓</span>
                  {planLimits[0]}
                </li>
                <li style={{ 
                  padding: '0.5rem 0', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <span style={{ 
                    color: '#10b981', 
                    marginRight: '0.5rem',
                    fontSize: '1.25rem'
                  }}>✓</span>
                  {planLimits[1]}
                </li>
              </ul>

              <button
                onClick={() => {
                  if (loading || (subscription && subscription.plan === plan.id)) return;
                  if (plan.paddlePriceId) {
                    handleSubscribe(plan.paddlePriceId, plan.id);
                  } else if (plan.id === 'free' && subscription && subscription.plan !== 'free') {
                    // Handle downgrade to free plan
                    handleSubscribe('free', plan.id);
                  }
                }}
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={loading || !!(subscription && subscription.plan === plan.id)}
              >
                {loadingPlanId === plan.id ? 'Processing...' : getButtonText(plan)}
              </button>
            </div>
          )})}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <a href="/account" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Back to Account
          </a>
        </div>
      </div>
    </div>
  )
}

export default SubscribePage
